import {Fragment, useEffect, useState} from "react";
import {
    BreadCrumb, CenteredModal, FormInput, DropdownAction,
    SkeletonTableLoading, Table, HtmlCodeEditor
} from "~/components";
import {format} from "date-fns";
import {faEdit, faEye} from "@fortawesome/free-solid-svg-icons";
import EmailTemplateAPI from '~/api/emailTemplateApi';
import {useForm, Controller} from "react-hook-form";
import {toast} from "react-toastify";
const breadCrumbItem = [
    { text: "Home", to: "/ai-chatbot/administrator", isActive: false },
    { text: "Email Template", to: "", isActive: true },
]
function EmailTemplate() {
    const [reloadTable, setReloadTable] = useState(false);
    const [emailTemplates, setEmailTemplates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isViewMode, setIsViewMode] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [previewContent, setPreviewContent] = useState("");
    const tableConfig = {
        columns: [
            {
                name: 'ID',
                width: '70px',
                className: 'fw-bold',
                key: 'EmailTemplateId'
            },
            {
                name: 'NOTIFICATION EVENT ID',
                width: '170px',
                className: 'fw-bold text-center',
                headerClassName: 'text-center',
                key: 'NotificationEventId'
            },
            {
                name: 'EVENT NAME',
                width: '250px',
                key: 'EventName'
            },
            {
                name: 'SUBJECT',
                width: '250px',
                key: 'Subject'
            },
            {
                name: 'MODIFIED BY',
                width: '150px',
                key: 'ModifiedBy'
            },
            {
                name: 'MODIFIED DATE',
                width: '150px',
                key: 'ModifiedDate',
                template: (row) => {
                    return (
                        <span>{format(new Date(row.ModifiedDate), "dd-MM-yyyy")}</span>
                    )
                }
            },
            {
                name: 'ACTION',
                width: '70px',
                key: 'Action',
                className: 'text-center',
                headerClassName: 'text-center',
                template: (row) => {
                    return (
                        <div className="dropdown d-inline-block">
                            <DropdownAction dropdownItems={[
                                { text: "View Details", icon: faEye, onClick: () => handleShowActionModal(row.EmailTemplateId, true) },
                                { text: "Edit", icon: faEdit, onClick: () => handleShowActionModal(row.EmailTemplateId, false) }
                            ]} isVerticalIcon={false}/>
                        </div>
                    )
                }
            }
        ],
        tableKey: "TableEmailTemplate",
        hasShowHideColumn: false
    }

    useEffect(() => {
        setLoading(true);
        EmailTemplateAPI.getAll()
            .then((res) => {
                setEmailTemplates(res.data);
            })
            .catch((err) => {
                ;
            })
            .finally(() => {
                setLoading(false);
            })
    }, [reloadTable]);

    const {
        register,
        handleSubmit,
        reset,
        control,
        getValues,
        formState: { errors }
    } = useForm();

    const handleShowActionModal = (id, viewMode) => {
        setIsViewMode(viewMode);
        EmailTemplateAPI.getById(id)
            .then((res) => {
                reset(res.data);
                setShowModal(true);
            })
            .catch((err) => {
                ;
            })
    }

    useEffect(() => {
        const previewTab = document.getElementById("tab-preview-template-tab");
        const handleTabShown = (event) => {
            if (event.target.id === "tab-preview-template-tab") {
                const currentHtml = getValues("TemplateContent");
                setPreviewContent(currentHtml);
            }
        };

        previewTab?.addEventListener("shown.bs.tab", handleTabShown);

        return () => {
            previewTab?.removeEventListener("shown.bs.tab", handleTabShown);
        };
    }, [getValues, showModal]);


    const onSubmitFrom = (data) => {
        EmailTemplateAPI.update(data)
            .then((res) => {
                res.data?.Success ?
                    toast.success(<span className="fs-9 fw-bold">Your email template has been updated.</span>)
                    : toast.error(<span className="fs-9 fw-bold">An error occurred while updating your email template.</span>)
            })
            .catch((err) => {
                toast.error(<span className="fs-9 fw-bold">An error occurred while updating your email template.</span>)
            })
            .finally(() => {
                setShowModal(false);
                reset({
                    Subject: "",
                    TemplateContent: ""
                })
            })
    }

    return (
        <Fragment>
            <BreadCrumb breadCrumbItem={breadCrumbItem}/>
            <h2 className="text-bold text-body-emphasis mb-3">Email Template</h2>
            {loading ? (
                <SkeletonTableLoading columns={tableConfig.columns} rows={10} />
            ) : (
                <Table tableConfig={{...tableConfig, data : emailTemplates}}/>
            )}
            <CenteredModal
                show={showModal}
                onClose={() => {
                    setPreviewContent("");
                    setShowModal(false)
                }}
                title={isViewMode ? "Email Template Details" : "Edit Email Template"}
                closeText={isViewMode ? "Close" : "Cancel"}
                isViewMode={isViewMode}
                formId="formActionPageRoute"
                modalType="modal-lg"
            >
                <form onSubmit={handleSubmit(onSubmitFrom)} id="formActionPageRoute">
                    <div className="mb-3">
                        <FormInput showHeaderLabel={true}
                                   name = "Subject"
                                   errors={errors}
                                   required={true}
                                   label="Assign Subject"
                                   disabled={isViewMode}
                                   validation={{...register("Subject", {
                                           required: `Subject is required.`
                                       })}}
                        />
                    </div>
                    <div className="mb-3">
                        <div className="mb-3">
                            <ul className="nav nav-underline mb-3" role="tablist">
                                <li className="nav-item">
                                    <a
                                        className="nav-link active"
                                        id="tab-template-content-tab"
                                        data-bs-toggle="tab"
                                        href="#tab-template-content"
                                        role="tab"
                                        aria-controls="tab-template-content"
                                        aria-selected="true"
                                    >
                                        Template Content
                                    </a>
                                </li>
                                <li className="nav-item">
                                    <a
                                        className="nav-link"
                                        id="tab-preview-template-tab"
                                        data-bs-toggle="tab"
                                        href="#tab-preview-template"
                                        role="tab"
                                        aria-controls="tab-preview-template"
                                        aria-selected="false"
                                    >
                                        Preview Template
                                    </a>
                                </li>
                            </ul>

                            <div className="tab-content">
                                <div
                                    className="tab-pane fade show active"
                                    id="tab-template-content"
                                    role="tabpanel"
                                    aria-labelledby="tab-template-content-tab"
                                >
                                    <h6 className="mb-2">Assign Template Content(*)</h6>
                                    <Controller
                                        name="TemplateContent"
                                        control={control}
                                        rules={{required: "Template Content is required."}}
                                        render={({field}) => (
                                            <HtmlCodeEditor
                                                value={field.value}
                                                onChange={field.onChange}
                                                height="500px"
                                                errors={errors}
                                                name="TemplateContent"
                                                readOnly={isViewMode}
                                            />
                                        )}
                                    />
                                </div>

                                <div
                                    className="tab-pane fade"
                                    id="tab-preview-template"
                                    role="tabpanel"
                                    aria-labelledby="tab-preview-template-tab"
                                >
                                    {previewContent && (
                                        <div
                                            style={{
                                                border: "1px solid #ccc",
                                                padding: "10px",
                                                marginTop: "20px",
                                                background: "#fafafa",
                                            }}
                                            dangerouslySetInnerHTML={{
                                                __html: previewContent,
                                            }}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </CenteredModal>
        </Fragment>
    )
}
export default EmailTemplate;